import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import  LogoContainer from '../../../../../shared/components/LogoContainer'
import { uploadBudgets } from "../../../../../../app/store/slices/budgetSlice";
import  Spinner  from '../../../../../shared/components/Spinner';
import InstructionsModal from '../../../../../shared/components/InstructionsModal';
import { selectAccounts, fetchAccounts } from "../../../../../../app/store/slices/accountSlice";
import {toast} from 'react-toastify';


const BudgetUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const fileInputRef = useRef();
  const accounts = useSelector(selectAccounts);

  const [selectedCanvasId] = useState(
    JSON.parse(localStorage.getItem('onboardingCanvas'))?._id ||
    location.state?.canvasId
  );

  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [showInstructions, setShowInstructions] = useState(false);

   useEffect(() => {
        if (selectedCanvasId) {
          dispatch(fetchAccounts({ canvasId:selectedCanvasId, limit:1000 }));
        }
  }, [dispatch, selectedCanvasId]);

  const handleFile = (file) => {
    setSelectedFile(file);
    setUploadError("");
    setUploadSuccess("");
  };

  // Handle input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    setUploadError("");
    setUploadSuccess("");
    if (!selectedCanvasId) {
      setUploadError("No canvas selected.");
      return;
    }
    if (!selectedFile) {
      setUploadError("Please select a file to upload.");
      return;
    }
    setIsUploading(true);
    try {
      await dispatch(
        uploadBudgets({
          canvasId: selectedCanvasId,
          file: selectedFile,
        })
      ).unwrap();
      setUploadSuccess("Budgets uploaded successfully!");
      setSelectedFile(null);
      navigate('/saving');
    } catch (err) {
      setUploadError(
        err?.message ||
        err?.toString() ||
        "Failed to upload budgets. Please check your file and try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    try {
      const sampleAccountName = accounts.length > 0 ? accounts[0].name : 'account_name_here';
      
      const templateData = [
        { 
          itemName: 'Rent', 
          category: 'Housing', 
          frequency: 'Monthly', 
          allocationPercent: '30', 
          plannedCost: '1500', 
          actualCost: '', 
          paymentAccountId: sampleAccountName,
          tags: ''
        },
        { 
          itemName: 'Netflix', 
          category: 'Entertainment', 
          frequency: 'Monthly', 
          allocationPercent: '2', 
          plannedCost: '15', 
          actualCost: '15', 
          paymentAccountId: sampleAccountName,
          tags: 'Subscription'
        }
      ];

      const csvContent = [
        'itemName,category,frequency,allocationPercent,plannedCost,actualCost,paymentAccountId,tags',
        ...templateData.map(row => 
          `${row.itemName},${row.category},${row.frequency},${row.allocationPercent},${row.plannedCost},${row.actualCost},${row.paymentAccountId},${row.tags}`
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `budget_template.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url); 
      
      setShowInstructions(false);
    } catch (error) {
      toast.error('Failed to download template');
      console.error('Template download error:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <LogoContainer/>
      <div className="card-setup">
        <h2 className="txt-page-heading">Upload your Fixed Expenses</h2>
        <p className="p-tag-below-heading text-center mb-8">
          Upload your file and it must match your fixed cost with the following columns or as in csv.<br />
          you can verify, correct and integrate the details once you proceed.
        </p>
        <div className="w-full flex flex-col items-center">
          <button
            type="button"
            className="btn-back w-64"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={isUploading}
          >
            Select File
          </button>
          <span className="text-gray-500 mb-4 font-medium">or</span>
          <div
            className={`rounded-md px-8 py-8 w-64 flex items-center justify-center text-center font-medium cursor-pointer border-2 transition
              ${dragActive ? "border-btn-primary bg-blue-50 text-blue-700" : "bg-gray-100 border-dashed border-btn-primary text-black"}`}
            style={{ minHeight: 60 }}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {selectedFile
              ? (
                <span className="truncate w-full">
                  <span className="font-semibold">Selected:</span> {selectedFile.name}
                </span>
              )
              : dragActive
                ? "Drop File Here"
                : "Drag & Drop"
            }
          </div>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".csv,.xlsx,.xls,.json,.pdf"
            onChange={handleFileChange}
            disabled={isUploading}
          />
          {/* Error/Success messages */}
          {uploadError && (
            <div className="text-red-600 text-sm font-semibold mt-4">{uploadError}</div>
          )}
          {uploadSuccess && (
            <div className="text-green-600 text-sm font-semibold mt-4">{uploadSuccess}</div>
          )}
          <button
            type="button"
            className="mt-4 text-btn-primary underline text-sm"
            onClick={() => setShowInstructions(true)}
          >
            View Upload Instructions
          </button>
        </div>
        <div className="flex flex-row justify-between items-center w-full mt-12">
          <button
            className="btn-back"
            onClick={() => navigate(-1)}
            type="button"
            disabled={isUploading}
          >
            Back
          </button>
          <button
            className="btn-continue"
            onClick={handleUpload}
            type="button"
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? (
              <Spinner text="Uploading..."/>
            ):(
              "Continue"
            )

              }
          </button>
        </div>
      </div>
      {/* Instructions Modal */}
      <InstructionsModal
        show={showInstructions}
        onClose={() => setShowInstructions(false)}
        type="budget"
        onDownloadTemplate={handleDownloadTemplate}
      />
    </div>
  );
};

export default BudgetUpload;